import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import CheckboxInput from './CheckboxInput';
import { applyShadowToken, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensSetPropType, selectSystemProps, selectTokens, viewProps } from '../utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectInputStyles = (_ref, isChecked) => {
  let {
    iconBackgroundColor,
    inputBorderColor,
    inputBorderRadius,
    inputBorderWidth,
    inputBackgroundColor,
    inputHeight,
    inputOutlineColor,
    inputOutlineWidth,
    inputOutlineOffset,
    inputShadow,
    inputWidth
  } = _ref;
  return {
    borderColor: inputBorderColor,
    borderWidth: inputBorderWidth,
    borderRadius: inputBorderRadius,
    backgroundColor: isChecked ? iconBackgroundColor : inputBackgroundColor,
    height: inputHeight,
    width: inputWidth,
    ...applyShadowToken(inputShadow),
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineColor: inputOutlineColor,
        outlineWidth: inputOutlineWidth,
        outlineOffset: inputOutlineOffset
      }
    })
  };
};
const selectIconTokens = _ref2 => {
  let {
    icon,
    iconColor,
    iconSize
  } = _ref2;
  return {
    icon,
    color: iconColor,
    size: iconSize
  };
};
const tokenKeys = ['checkedBackgroundColor', 'checkedSize', 'inputBackgroundColor', 'inputBorderColor', 'inputBorderWidth', 'inputOutlineColor', 'inputOutlineWidth', 'inputSize', 'outerBorderColor', 'outerBorderWidth', 'outerBorderGap', 'inputSize'];
export const selectCheckboxTokens = (themeTokens, prefix) => selectTokens(tokenKeys, themeTokens, prefix);

/**
 * The visual toggle of a checkbox input. Is not interactive on its own, should be used inside
 * an interactive parent that passes down props when interacted with. Used in CheckboxCard
 */
const CheckboxButton = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    isChecked,
    tokens,
    inactive,
    defaultChecked,
    inputId,
    iconId,
    isControlled,
    handleChange,
    name: inputName,
    value,
    ...rest
  } = _ref3;
  const {
    icon: IconComponent,
    ...stateTokens
  } = useThemeTokens('Checkbox', tokens, {
    checked: isChecked
  });
  const iconTokens = selectIconTokens(stateTokens);
  return /*#__PURE__*/_jsx(View, {
    ...selectProps(rest),
    children: /*#__PURE__*/_jsxs(View, {
      style: [staticStyles.defaultInputStyles, selectInputStyles(stateTokens, isChecked)],
      testID: inputId,
      children: [/*#__PURE__*/_jsx(CheckboxInput, {
        ref: ref,
        checked: isChecked,
        defaultChecked: defaultChecked,
        disabled: inactive,
        id: inputId,
        isControlled: isControlled,
        onChange: handleChange,
        name: inputName,
        value: value
      }), isChecked && IconComponent && /*#__PURE__*/_jsx(View, {
        testID: iconId,
        children: /*#__PURE__*/_jsx(IconComponent, {
          ...iconTokens
        })
      })]
    })
  });
});
CheckboxButton.displayName = 'CheckboxButton';
CheckboxButton.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Use `checked` for controlled Checkbox. For uncontrolled Checkbox, use the `defaultChecked` prop.
   */
  isChecked: PropTypes.bool,
  /**
   * Checkbox tokens.
   */
  tokens: getTokensSetPropType(tokenKeys, {
    allowFunction: true
  }),
  /**
   * Whether the corresponding input is disabled or active.
   */
  inactive: PropTypes.bool,
  /**
   * Use `defaultChecked` to provide the initial value for an uncontrolled Checkbox.
   */
  defaultChecked: PropTypes.bool,
  /**
   * Checkbox input ID.
   */
  inputId: PropTypes.string,
  /**
   * Checkbox icon ID.
   */
  iconId: PropTypes.string,
  /**
   * Can control the checkbox on the card.
   */
  isControlled: PropTypes.bool,
  /**
   * Callback called when a controlled checkbox gets interacted with.
   */
  handleChange: PropTypes.func,
  /**
   * Associate this checkbox with a group (set as the name attribute).
   */
  name: PropTypes.string,
  /**
   * The value of the checkbox: true or false
   */
  value: PropTypes.bool
};
export default CheckboxButton;
const staticStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  defaultInputStyles: {
    alignItems: 'center',
    justifyContent: 'center'
  }
});